import React, {useEffect, useState} from "react";
import {useTheme} from "@mui/material/styles";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import {useNavigate, useLocation} from "react-router-dom";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import {Alert, Snackbar, Grid, TextField, Card, AppBar, Typography} from "@mui/material";
import MDButton from "components/MDButton";
import {POST} from "APIclient";
import {LocalizationProvider, DatePicker} from "@mui/x-date-pickers";
import {AdapterDayjs} from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";

function AdminExtendProbation() {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const employee = location.state?.employee || {};

  const [open, setOpen] = useState(false);
  const [snakColor, setSnakColor] = useState("");
  const [snakMessage, setSnakMessage] = useState("");
  const [isButtonDisabled, setIsButtonDisabled] = useState(true);
  const [formData, setFormData] = useState({
    newProbationEndDate: "",
    reason: ""
  });

  const [errors, setErrors] = useState({
    newProbationEndDate: "",
    reason: ""
  });

  useEffect(() => {
    setIsButtonDisabled(!(formData.newProbationEndDate && formData.reason.trim()));
  }, [formData.newProbationEndDate, formData.reason]);

  const handleClose = () => setOpen(false);

  const handleChange = event => {
    const {name, value} = event.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));

    setErrors(prevErrors => ({
      ...prevErrors,
      [name]: ""
    }));
  };

  const validateFields = () => {
    let tempErrors = {};
    let isValid = true;

    if (!formData.newProbationEndDate) {
      tempErrors.newProbationEndDate = "New probation end date is required.";
      isValid = false;
    }

    if (!formData.reason.trim()) {
      tempErrors.reason = "Reason is required.";
      isValid = false;
    }

    setErrors(tempErrors);
    return isValid;
  };

  const handleSubmit = async () => {
    if (!validateFields()) {
      return;
    }
    const url = `admin/extendProbation`;
    const payload = {
      empId: employee.empId,
      probation_end_date: formData.newProbationEndDate,
      reason: formData.reason
    };
    try {
      const response = await POST(url, payload);
      if (response.status === 200) {
        setSnakColor("success");
        setSnakMessage("Probation extended successfully");
        setFormData({
          newProbationEndDate: "",
          reason: ""
        });
        setTimeout(() => {
          navigate("/employeeProbationReports");
        }, 2000);
      } else {
        setSnakColor("error");
        setSnakMessage("Failed to extend probation");
      }
    } catch (error) {
      console.error("Error extending probation:", error);
      setSnakColor("error");
      setSnakMessage("Error extending probation");
    } finally {
      setOpen(true);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <MDBox container sx={{minHeight: "100vh", width: "98%"}}>
          <AppBar position="static">
            <Card>
              <MDBox
                pt={2}
                mb={0}
                pb={4}
                px={4}
                sx={{borderRadius: "5px", border: "1px solid #D3D3D3"}}
              >
                <Typography sx={{fontSize: "16px", fontWeight: "500"}} color="textPrimary">
                  {" "}
                  General Details
                </Typography>
                <MDBox display="flex" flexDirection="column" pt={2}>
                  <Grid container spacing={3} alignItems="center">
                    {/* Employee ID & Name in One Row */}
                    <Grid item xs={6} container alignItems="center">
                      <MDTypography
                        variant="caption"
                        sx={{fontSize: "14px", fontWeight: "500", minWidth: "150px"}}
                      >
                        Employee ID:
                      </MDTypography>
                      <MDTypography variant="caption" sx={{fontSize: "14px", fontWeight: "400"}}>
                        {employee.empId || "-"}
                      </MDTypography>
                    </Grid>
                    <Grid item xs={6} container alignItems="center">
                      <MDTypography
                        variant="caption"
                        sx={{fontSize: "14px", fontWeight: "500", minWidth: "150px"}}
                      >
                        Employee Name:
                      </MDTypography>
                      <MDTypography variant="caption" sx={{fontSize: "14px", fontWeight: "400"}}>
                        {employee.empName || "-"}
                      </MDTypography>
                    </Grid>

                    {/* Probation Start & End Date in One Row */}
                    <Grid item xs={6} container alignItems="center">
                      <MDTypography
                        variant="caption"
                        sx={{fontSize: "14px", fontWeight: "500", minWidth: "150px"}}
                      >
                        Probation Start Date:
                      </MDTypography>
                      <MDTypography variant="caption" sx={{fontSize: "14px", fontWeight: "400"}}>
                        {employee.probationStartDate || "-"}
                      </MDTypography>
                    </Grid>
                    <Grid item xs={6} container alignItems="center">
                      <MDTypography
                        variant="caption"
                        sx={{fontSize: "14px", fontWeight: "500", minWidth: "150px"}}
                      >
                        Probation End Date:
                      </MDTypography>
                      <MDTypography variant="caption" sx={{fontSize: "14px", fontWeight: "400"}}>
                        {employee.probationEndDate || "-"}
                      </MDTypography>
                    </Grid>

                    {/* Designation in its Own Row */}
                    <Grid item xs={6} container alignItems="center">
                      <MDTypography
                        variant="caption"
                        sx={{fontSize: "14px", fontWeight: "500", minWidth: "150px"}}
                      >
                        Designation:
                      </MDTypography>
                      <MDTypography variant="caption" sx={{fontSize: "14px", fontWeight: "400"}}>
                        {employee.designation || "-"}
                      </MDTypography>
                    </Grid>
                  </Grid>
                </MDBox>
              </MDBox>
            </Card>
          </AppBar>

          <MDBox pt={1}>
            <Card sx={{borderRadius: "5px", border: "1px solid #D3D3D3", background: "#fff", p: 4}}>
              <Typography pb={2} sx={{fontSize: "16px", fontWeight: "500"}} color="textPrimary">
                {" "}
                Extend Probation Period{" "}
              </Typography>
              <Grid container spacing={2}>
                {/* New Probation End Date (Half Width) */}
                <Grid item xs={6}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      format="DD/MM/YYYY"
                      sx={{width: "83%"}}
                      label="New Probation End Date*"
                      value={
                        formData.newProbationEndDate ? dayjs(formData.newProbationEndDate) : null
                      }
                      onChange={date =>
                        setFormData(prevState => ({
                          ...prevState,
                          newProbationEndDate: date ? date.format("YYYY-MM-DD") : ""
                        }))
                      }
                      slotProps={{
                        textField: {
                          error: !!errors.newProbationEndDate,
                          helperText: errors.newProbationEndDate
                        }
                      }}
                    />
                  </LocalizationProvider>
                </Grid>

                {/* Empty Grid to Push Reason to Next Line */}
                <Grid item xs={6}></Grid>

                {/* Reason Field (Full Width on Next Row) */}
                <Grid item xs={5}>
                  <TextField
                    fullWidth
                    label="Reason*"
                    name="reason"
                    multiline
                    rows={3}
                    value={formData.reason}
                    onChange={handleChange}
                    error={!!errors.reason}
                    helperText={errors.reason}
                  />
                </Grid>

                <Grid item xs={12} sx={{display: "flex", justifyContent: "flex-end"}}>
                  <MDButton
                    variant="gradient"
                    color="info"
                    onClick={handleSubmit}
                    disabled={isButtonDisabled}
                  >
                    Extend
                  </MDButton>
                </Grid>
              </Grid>
            </Card>
          </MDBox>
          <Snackbar
            open={open}
            anchorOrigin={{vertical: "top", horizontal: "right"}}
            autoHideDuration={1200}
            onClose={handleClose}
          >
            <Alert onClose={handleClose} severity={snakColor} variant="filled">
              {snakMessage}
            </Alert>
          </Snackbar>
        </MDBox>
      </MDBox>
    </DashboardLayout>
  );
}

export default AdminExtendProbation;
